import { EntityTypeEnum, SubscriptionFilter, SubscriptionIdentifier, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import React, { useState } from 'react'
import { SubscriptionDetail } from 'store/selection/selectionTypes'
import { filterWeekend } from 'utils'
import {
  getTextFlagButtonState,
  TextButton,
  TextButtonType,
  TextFlagButtonsContainer,
  ToolbarTextFlagButton
} from 'view/components/buttons'
import { useChangedSubscriptionFilters } from 'view/shared/entity/hooks/useChangedSubscriptionFilters'
import { DataGridFiltersWrapper, FieldsWrapper, FiltersSlideDown, ToolbarToggleWrapper } from './filtersStyles'
import {
  currentAndActiveSubscriptionFiltersAreEqual,
  dynamicDateSelected,
  getDateRangeSubscriptionFilters,
  getDateRangeSubscriptionFilterValue,
  getDynamicDateFilters,
  getEndDateEqualsFilterValues
} from './utils'
import { SubscriptionDateRangeComponent, SubscriptionDates } from './SubscriptionFilterComponents/SubscriptionDateRange'
import { DynamicDateSubscriptionButtons } from './SubscriptionFilterComponents/DynamicDateSubscriptionButtons'
import { isNotEmpty } from 'utils/predicate'
import { isEmpty } from 'lodash'

interface RollsDealsSubscriptionFiltersProps {
  subscriptionDetail: SubscriptionDetail | undefined
}

const rollsSubscriptionFiltersFieldNames = ['endDate']

export const RollsDealsSubscriptionFilters: React.FC<RollsDealsSubscriptionFiltersProps> = ({ subscriptionDetail }) => {
  const activeSubscriptionFilters = subscriptionDetail?.filters || []
  const activeRollsSubscriptionFilters = activeSubscriptionFilters.filter(activeSubscriptionFilter =>
    rollsSubscriptionFiltersFieldNames.includes(activeSubscriptionFilter.fieldName)
  )
  const endDateFilterValues = getEndDateEqualsFilterValues(subscriptionDetail)
  const isCustomDate = !dynamicDateSelected(endDateFilterValues)
  const [subscriptionFilters, setSubscriptionFilters] = useState<SubscriptionFilter[]>(isCustomDate ? activeRollsSubscriptionFilters : [])
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([])
  const onChangeSubscription = useChangedSubscriptionFilters(EntityTypeEnum.DEAL, SubscriptionIdentifier.ROLLS, true)
  const searchButtonDisabled =
    isNotEmpty(validationIssues) ||
    currentAndActiveSubscriptionFiltersAreEqual(subscriptionFilters, activeRollsSubscriptionFilters) ||
    isEmpty(subscriptionFilters)

  const handleDynamicDateSubscription = (values: string[]) => {
    onChangeSubscription(getDynamicDateFilters(values))
  }

  const handleCustomButtonClick = () => {
    onChangeSubscription([])
  }

  const handleDateRangeUpdate = (subscriptionDates: SubscriptionDates, validationIssues: ValidationIssue[]) => {
    setValidationIssues(validationIssues)
    setSubscriptionFilters(getDateRangeSubscriptionFilters(subscriptionDates, 'endDate', subscriptionFilters))
  }

  const handleSearch = () => {
    onChangeSubscription(subscriptionFilters)
  }

  return (
    <DataGridFiltersWrapper>
      <ToolbarToggleWrapper>
        <div>{isCustomDate && Locale.translate(Translation.TC_CUSTOM)}</div>
        <TextFlagButtonsContainer>
          <DynamicDateSubscriptionButtons onClick={handleDynamicDateSubscription} currentFilterDates={endDateFilterValues} />
          <ToolbarTextFlagButton
            dataTestId={'RollsDealsSubscriptionFilters-ToolbarTextFlagButton-ButtonCustom'}
            state={getTextFlagButtonState(isCustomDate)}
            onClick={handleCustomButtonClick}>
            {Locale.translate(Translation.TC_CUSTOM)}
          </ToolbarTextFlagButton>
        </TextFlagButtonsContainer>
      </ToolbarToggleWrapper>
      <FiltersSlideDown closed={isCustomDate ? false : true}>
        <div>
          <FieldsWrapper>
            <SubscriptionDateRangeComponent
              label={Translation.TC_END_DATE}
              onUpdate={handleDateRangeUpdate}
              minDate={new Date()}
              filterDate={filterWeekend}
              initialSubscriptionDates={getDateRangeSubscriptionFilterValue(subscriptionFilters, 'endDate')}
            />
          </FieldsWrapper>
        </div>
        <TextButton
          dataTestId={'RollsDealsSubscriptionFilters-ButtonSearch'}
          type={TextButtonType.Primary}
          disabled={searchButtonDisabled}
          onClick={handleSearch}>
          {Locale.translate(Translation.TC_BUTTON_SEARCH)}
        </TextButton>
      </FiltersSlideDown>
    </DataGridFiltersWrapper>
  )
}
